import { FREE_READ_LIMIT } from '@archetelos/lib/config';
import { queryOptions } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';

// Metered-archive read gate seam (UX audit Spec 01). Mirrors freemium.ts (the
// VOD gate) over a SEPARATE monthly read allowance: free_read_status /
// consume_free_read. The RPCs are the authoritative gate; this is the client
// read for the wall countdown + the explicit spend the wall's "Read the rest"
// button calls.

// Re-exported so existing app imports keep resolving; the number itself now
// lives in @archetelos/lib/config, shared with the marketing gate card.
export { FREE_READ_LIMIT };

export interface FreeReadStatus {
  isMember: boolean;
  used: number;
  remaining: number;
  unlockedIds: string[];
}

export const freemiumReadQueries = {
  /** Enabled only when signed in; anon has no allowance. */
  status: (signedIn: boolean) =>
    queryOptions({
      queryKey: ['freemium-reads', 'status'] as const,
      queryFn: async (): Promise<FreeReadStatus> => {
        const { data, error } = await supabase.rpc('free_read_status');
        if (error) throw error;
        const row = data?.[0];
        return {
          isMember: row?.is_member ?? false,
          used: row?.used ?? 0,
          remaining: row?.remaining ?? FREE_READ_LIMIT,
          unlockedIds: row?.unlocked_ids ?? [],
        };
      },
      enabled: signedIn,
      staleTime: 1000 * 10,
    }),
};

/** Authoritative server-side read spend; the wall's "Read the rest" calls this. */
export async function consumeFreeRead(
  contentId: string,
): Promise<{ allowed: boolean; remaining: number | null }> {
  const { data, error } = await supabase.rpc('consume_free_read', { p_content_id: contentId });
  if (error) throw error;
  const row = data?.[0];
  return { allowed: row?.allowed ?? false, remaining: row?.remaining ?? null };
}
